/* Memory: the self-building knowledge graph. Dumps are fire-and-forget — the
   agent ingests asynchronously, so the store polls the dump until it lands
   and then refreshes the graph. Queries stream their answer token-by-token. */
import { defineStore } from 'pinia'
import {
  memoryApi,
  type AgentAction, type CitedNode, type MemoryDump, type MemoryEdge, type MemoryNode,
  type MemoryNodeDetail, type RollbackResult,
} from '@/lib/memoryApi'
import { useToasts } from '@/stores/toasts'

function toastError(text: string) {
  useToasts().push({ title: 'Memory', text, color: 'var(--danger)', icon: 'memory' })
}

/* Debounce + staleness for the server-side search; kept out of reactive state. */
let searchTimer: ReturnType<typeof setTimeout> | null = null

/** kind → accent, from the design tokens (free-form kinds fall back to gold). */
export const KIND_COLORS: Record<string, string> = {
  person: 'var(--p-open)',
  team: 'var(--sage)',
  project: 'var(--gold)',
  concept: 'var(--p-cowork)',
  decision: 'var(--p-meeting)',
  event: 'var(--p-break)',
  tool: 'var(--p-deep)',
  place: 'var(--p-away)',
  fact: 'var(--text-mid)',
}

export function kindColor(kind: string): string {
  return KIND_COLORS[kind] || 'var(--gold)'
}

interface MemoryState {
  nodes: MemoryNode[]
  edges: MemoryEdge[]
  graphLoaded: boolean
  graphLoading: boolean
  dumps: MemoryDump[]
  dumping: boolean
  /** Per-dump agent actions ("searched…", "created…") — live while ingesting. */
  activity: Record<number, AgentAction[]>
  pollTimer: ReturnType<typeof setTimeout> | null
  selectedId: string | null
  detail: MemoryNodeDetail | null
  detailLoading: boolean
  filter: string
  /** Node ids the server's full-text search matched (content too, not just the
      loaded summaries) — folded into matchedIds. */
  serverMatchIds: Set<string>
  /** Of the server matches, the ids that matched ONLY semantically — ranked
      below direct (lexical) matches so an exact hit is never pushed down. */
  semanticIds: Set<string>
  /** A debounced server search is in flight (for a "Searching…" affordance). */
  searching: boolean
  question: string
  answer: string
  cited: CitedNode[]
  asking: boolean
  askError: string
  askAbort: AbortController | null
}

export const useMemory = defineStore('memory', {
  state: (): MemoryState => ({
    nodes: [],
    edges: [],
    graphLoaded: false,
    graphLoading: false,
    dumps: [],
    dumping: false,
    activity: {},
    pollTimer: null,
    selectedId: null,
    detail: null,
    detailLoading: false,
    filter: '',
    serverMatchIds: new Set(),
    semanticIds: new Set(),
    searching: false,
    question: '',
    answer: '',
    cited: [],
    asking: false,
    askError: '',
    askAbort: null,
  }),

  getters: {
    /** Node ids matching the filter — the instant client-side pass over loaded
        fields (title/summary/alias/slug) unioned with the server's full-text
        hits (which reach into node content), kept to nodes actually loaded. */
    matchedIds(s): Set<string> {
      const q = s.filter.trim().toLowerCase()
      if (!q) return new Set()
      const out = new Set<string>()
      for (const n of s.nodes) {
        if (
          n.title.toLowerCase().includes(q) ||
          n.id.includes(q) ||
          n.summary.toLowerCase().includes(q) ||
          n.aliases.some((a) => a.toLowerCase().includes(q))
        )
          out.add(n.id)
      }
      if (s.serverMatchIds.size) {
        const known = new Set(s.nodes.map((n) => n.id))
        for (const id of s.serverMatchIds) if (known.has(id)) out.add(id)
      }
      return out
    },
    pendingDumps: (s): MemoryDump[] => s.dumps.filter((d) => d.status === 'pending' || d.status === 'processing'),
  },

  actions: {
    async loadGraph(quiet = false) {
      if (!quiet) this.graphLoading = true
      try {
        const g = await memoryApi.graph()
        this.nodes = g.nodes
        this.edges = g.edges
        this.graphLoaded = true
      } catch (e) {
        if (!quiet) toastError(e instanceof Error ? e.message : 'Could not load the memory graph')
      } finally {
        this.graphLoading = false
      }
    },

    /** Debounced server-side full-text search for the filter box. Instant local
        highlighting (matchedIds) already fired on keystroke; this folds in the
        content matches the loaded graph can't see. No AI — just Postgres FTS. */
    runSearch() {
      if (searchTimer) clearTimeout(searchTimer)
      const q = this.filter.trim()
      if (!q) {
        this.serverMatchIds = new Set()
        this.semanticIds = new Set()
        this.searching = false
        return
      }
      this.searching = true
      searchTimer = setTimeout(async () => {
        searchTimer = null
        try {
          const { nodes, semantic } = await memoryApi.search(q)
          // Drop a stale response if the filter moved on while we waited.
          if (this.filter.trim() !== q) return
          this.serverMatchIds = new Set(nodes.map((n) => n.id))
          this.semanticIds = new Set(semantic ?? [])
        } catch {
          /* the instant local filter still works; server search is a bonus */
        } finally {
          if (this.filter.trim() === q) this.searching = false
        }
      }, 250)
    },

    async loadDumps() {
      try {
        this.dumps = (await memoryApi.dumps(20)).dumps
        this.schedulePoll()
      } catch {
        /* feed is decorative; the toast would be noise */
      }
    },

    /** Commit a piece of information to memory; the agent takes it from there.
        followLinks lets the agent also explore pages linked from dumped URLs. */
    async dump(text: string, followLinks = false): Promise<boolean> {
      const t = text.trim()
      if (!t || this.dumping) return false
      this.dumping = true
      try {
        const d = await memoryApi.dump(t, followLinks)
        this.dumps = [d, ...this.dumps].slice(0, 20)
        this.schedulePoll()
        return true
      } catch (e) {
        toastError(e instanceof Error ? e.message : 'Could not save to memory')
        return false
      } finally {
        this.dumping = false
      }
    },

    /** Commit one or more files (with an optional note) to memory. */
    async upload(files: File[], text = '', followLinks = false): Promise<boolean> {
      if (!files.length || this.dumping) return false
      this.dumping = true
      try {
        const d = await memoryApi.upload(files, text.trim(), followLinks)
        this.dumps = [d, ...this.dumps].slice(0, 20)
        this.schedulePoll()
        return true
      } catch (e) {
        toastError(e instanceof Error ? e.message : 'Could not upload to memory')
        return false
      } finally {
        this.dumping = false
      }
    },

    /** The agent's actions for one dump, in plain terms. Polled while it
        ingests; fetched once on demand when reviewing afterwards. */
    async loadActivity(id: number) {
      try {
        this.activity[id] = (await memoryApi.dumpActivity(id)).actions
      } catch {
        /* the live ticker is decorative — next poll retries */
      }
    },

    /** While any dump is still being ingested, poll; on landing, refresh.
        Each tick also pulls the agent's activity so the user sees what it is
        doing in there, not just a spinner. */
    schedulePoll() {
      if (this.pollTimer) return
      if (!this.pendingDumps.length) return
      this.pollTimer = setTimeout(async () => {
        this.pollTimer = null
        const pending = this.pendingDumps
        let landed = false
        for (const d of pending) {
          try {
            const fresh = await memoryApi.dumpStatus(d.id)
            const i = this.dumps.findIndex((x) => x.id === d.id)
            if (i >= 0) this.dumps[i] = fresh
            if (fresh.status === 'done' || fresh.status === 'error') landed = true
            await this.loadActivity(d.id)
          } catch {
            /* retry next tick */
          }
        }
        if (landed) {
          void this.loadGraph(true)
          if (this.selectedId) void this.select(this.selectedId, true)
        }
        this.schedulePoll()
      }, 2500)
    },

    /** Undo everything one dump did to the graph (recorded change by change,
        applied in reverse server-side). Refreshes everything that shows it. */
    async rollback(id: number): Promise<RollbackResult | null> {
      try {
        const res = await memoryApi.rollbackDump(id)
        const i = this.dumps.findIndex((d) => d.id === id)
        if (i >= 0) this.dumps[i] = { ...this.dumps[i]!, rolled_back: true }
        void this.loadGraph(true)
        void this.loadDumps()
        if (this.selectedId) void this.select(this.selectedId, true)
        return res
      } catch (e) {
        toastError(e instanceof Error ? e.message : 'Could not roll back that dump')
        return null
      }
    },

    async select(id: string | null, quiet = false) {
      this.selectedId = id
      this.detail = null
      if (!id) return
      if (!quiet) this.detailLoading = true
      try {
        const d = await memoryApi.node(id)
        // Stale response guard: another node may have been picked meanwhile.
        if (this.selectedId === id) this.detail = d
      } catch (e) {
        if (!quiet) toastError(e instanceof Error ? e.message : 'Could not load that node')
      } finally {
        this.detailLoading = false
      }
    },

    async ask() {
      const q = this.question.trim()
      if (!q || this.asking) return
      this.askAbort?.abort()
      const abort = new AbortController()
      this.askAbort = abort
      this.asking = true
      this.answer = ''
      this.cited = []
      this.askError = ''
      try {
        this.cited = await memoryApi.query(q, (delta) => {
          if (!abort.signal.aborted) this.answer += delta
        }, abort.signal)
      } catch (e) {
        this.askError = e instanceof Error ? e.message : 'The memory agent could not answer'
      } finally {
        if (this.askAbort === abort) {
          this.asking = false
          this.askAbort = null
        }
      }
    },

    stopAsking() {
      this.askAbort?.abort()
      this.askAbort = null
      this.asking = false
    },
  },
})
